<template>
  <div class="navbar" :class="{ 'hidden-navbar': !navbarVisible }">
    <nav>
      <div class="userProfileLink">
        <AccountIdentityBalance
          v-if="user.loggedIn && userUid && userAuth"
          :user="userAuth"
          v-haptic
        />

        <router-link
          class="clickable"
          v-if="!user.loggedIn"
          :to="user.loggedIn ? (currentOrgUID ? '/dashboard' : '/') : '/'"
          v-haptic
        >
          <transition name="coin-flip" mode="out-in">
            <div class="row alignCentreRow" :key="displayKey">
              <CoinSymbol
                v-if="!logoUrl || !user.loggedIn || !isLogoValid"
                class="coin-symbol"
                size="big"
              />
              <img
                v-if="logoUrl && user.loggedIn && isLogoValid"
                :src="logoUrl"
                class="logo-image"
                alt="Logo"
                @error="handleImageError"
              />
            </div>
          </transition>
        </router-link>
      </div>

      <div class="navIconCont">
        <!-- Back Button -->
        <div
          v-if="user.loggedIn && $route.name !== 'home'"
          class="menu-item menu-link"
          @click="goBack"
        >
          <div class="column alignCentre iconColumn backArrow">
            <svg class="small">
              <use
                href="../../assets/icons/iconset.svg#arrowleft_carousel_big"
              ></use>
            </svg>
          </div>
        </div>
        <router-link
          v-if="user.loggedIn"
          v-haptic
          class="menu-item menu-link"
          :to="{ name: 'home' }"
        >
          <div
            :class="{ active: activeTab === 'ecosystem' }"
            v-haptic
            class="column alignCentre iconColumn"
          >
            <svg class="small">
              <use href="../../assets/icons/iconset.svg#shop"></use>
            </svg>
          </div>
        </router-link>

        <router-link
          v-if="user.loggedIn"
          v-haptic
          class="menu-item menu-link"
          :to="{ name: 'wallet' }"
        >
          <div
            :class="{ active: activeTab === 'wallet' }"
            v-haptic
            class="column alignCentre iconColumn wallet-icon"
          >
            <svg class="small">
              <use href="../../assets/icons/iconset.svg#wallet"></use>
            </svg>
          </div>
        </router-link>

        <div v-haptic class="menu-item menu-link" @click="toggleMessages">
          <div
            v-if="user.loggedIn"
            :class="{ active: areMessagesOpen }"
            class="column alignCentre iconColumn"
          >
            <svg class="small">
              <use href="../../assets/icons/iconset.svg#message"></use>
            </svg>
            <span v-if="unreadMessagesCount > 0" class="notification-dot">
              {{ unreadMessagesCount > 9 ? "+" : unreadMessagesCount }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="user.loggedIn" id="placeholderspaceformenuAbsolute"></div>

      <div class="row alignCentreRow">
        <NavbarMenu class="clickable" />
      </div>
    </nav>
  </div>
</template>

<script>
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { auth } from "@/firebase";
import debounce from "lodash.debounce";
import NavbarMenu from "./Menu.vue";
import CoinSymbol from "@/components/GenericComponents/CoinSymbol.vue";
import AccountIdentityBalance from "@/components/UserComponents/AccountIdentityBalance.vue";
export default {
  name: "Navbar",
  components: {
    NavbarMenu,
    CoinSymbol,
    AccountIdentityBalance,
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    let lastScrollPosition = 0;
    const goBack = () => {
      router.go(-1);
    };

    const navbarVisible = computed(() => store.state.navbarVisible);
    const menuOpen = computed(() => store.getters.menuOpen);
    const activeTab = computed(() => route.meta.activeTab || null);
    const monetaryParameters = computed(
      () => store.getters.monetary_parameters
    );
    const currentCoinSymbol = ref("");
    const coinKey = ref(0);
    const isRotating = ref(false);
    const rotateClass = ref("");
    const areMessagesOpen = computed(
      () => store.getters["UIState/areMessagesOpen"]
    );
    const user = computed(() => store.getters.user);
    const userUid = ref(null);
    const userAuth = ref(null);
    const currentOrgUID = computed(() => store.getters.selectedOrgUID);
    const selectedOrgOwnerUID = computed(
      () => store.getters.selectedOrgOwnerUID
    );
    const activeWallet = computed(() => store.getters.currentUserWallet);
    const activeWalletStatus = computed(
      () => store.getters.currentUserWalletStatus
    );
    const activeWalletOwnerUID = computed(
      () => store.state.userActions?.currentUserWalletOwnerUID || null
    );
    const activeWalletOrgUID = computed(
      () => store.state.userActions?.currentUserOrgUID || null
    );
    const logoUrl = computed(() => store.getters.getLogoUrl);
    const isLogoValid = ref(true);
    const displayKey = ref(0);
    const unreadMessagesCount = computed(
      () => store.getters["UIState/unreadMessagesCount"] || 0
    );

    // Ensure hooks are registered before any async operations
    onMounted(() => {
      window.addEventListener("scroll", debouncedHandleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", debouncedHandleScroll);
    });

    // Function to initialize the authentication state listener
    const initAuthStateListener = () => {
      const unsubscribeAuth = auth.onAuthStateChanged((user) => {
        if (user) {
          userUid.value = user.uid;
          userAuth.value = user;

          // App owns Auth/credential commits and membership bootstrap. Navbar
          // may resolve optional badges, but must not start a competing owner.
          fetchUserData(user.uid).catch(() => {});
        } else {
          userUid.value = null;
          userAuth.value = null;
        }
      });

      // Cleanup auth state listener on unmount
      onUnmounted(() => {
        unsubscribeAuth();
      });
    };

    // Store for unsubscribe functions
    const unsubscribeFunctions = ref([]);

    // Function to fetch user-related data
    const fetchUserData = async (uid) => {
      await store.dispatch("UIState/fetchUnreadMessagesCount", uid);
    };

    const setMonetaryPresentation = (monetaryParams = null) => {
      const payload = {
        coinName:
          monetaryParams?.coin_name || monetaryParams?.coinName || "Unit",
        coinSymbol:
          monetaryParams?.coin_symbol || monetaryParams?.coinSymbol || "Ʉ",
      };

      store.dispatch("updateCoinSymbol", {
        symbol: payload.coinSymbol,
        name: payload.coinName,
      });
      store.commit("SET_MONETARY_PARAMS", payload);
    };

    // App.vue owns one generation-guarded listener for the exact active wallet.
    // Only project it into the shell when both the authenticated UID and selected
    // organisation match that listener's ownership tags.
    const stopActiveWalletPresentationWatch = watch(
      () => ({
        uid: userUid.value,
        orgUID: currentOrgUID.value,
        walletOwnerUID: activeWalletOwnerUID.value,
        walletOrgUID: activeWalletOrgUID.value,
        walletStatus: activeWalletStatus.value,
        wallet: activeWallet.value,
      }),
      ({
        uid,
        orgUID,
        walletOwnerUID,
        walletOrgUID,
        walletStatus,
        wallet,
      }) => {
        const ownsActiveContext =
          !!uid &&
          !!orgUID &&
          uid === walletOwnerUID &&
          orgUID === walletOrgUID &&
          walletStatus === "ready";

        if (!ownsActiveContext || !wallet || wallet.isActiveMember === false) {
          setMonetaryPresentation();
          return;
        }

        setMonetaryPresentation(wallet.monetary_parameters);
      },
      { immediate: true }
    );

    const stopLogoContextWatch = watch(
      () => [
        userUid.value,
        currentOrgUID.value,
        selectedOrgOwnerUID.value,
      ],
      ([uid, orgUID, ownerUID]) => {
        isLogoValid.value = true;
        displayKey.value += 1;

        if (!uid || !orgUID || ownerUID !== uid) {
          store.dispatch("clearLogoContext");
          return;
        }

        store.dispatch("fetchLogoUrl", { uid, orgUID });
      },
      { immediate: true }
    );

    const stopLogoSourceWatch = watch(logoUrl, (nextLogoUrl, previousLogoUrl) => {
      if (nextLogoUrl !== previousLogoUrl) {
        isLogoValid.value = true;
        displayKey.value += 1;
      }
    });

    const handleImageError = () => {
      isLogoValid.value = false;
      displayKey.value += 1; // Update key to trigger transition to CoinSymbol
    };

    const toggleMessages = () => {
      store.dispatch("UIState/setMessagesMinimised", false);
      store.dispatch("UIState/setMessagesOpen", !areMessagesOpen.value);
      store.commit("SET_MENU_OPEN", false); // Close the menu when toggling messages
    };

    const handleScroll = () => {
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollThreshold = 10; // Set a threshold for the scroll

      // Ignore small scroll movements (like the bounce effect)
      if (
        Math.abs(currentScrollPosition - lastScrollPosition) < scrollThreshold
      ) {
        return;
      }

      if (currentScrollPosition < 0) return; // Prevent negative scroll values

      if (!menuOpen.value) {
        const shouldShowNavbar = currentScrollPosition < lastScrollPosition;
        store.dispatch("toggleNavbarVisibility", shouldShowNavbar);
      }

      lastScrollPosition = currentScrollPosition;
    };

    const debouncedHandleScroll = debounce(handleScroll, 15);

    // Initialize authentication listener on mounted
    onMounted(initAuthStateListener);

    // Clean up all subscriptions on unmount
    onUnmounted(() => {
      stopActiveWalletPresentationWatch();
      stopLogoContextWatch();
      stopLogoSourceWatch();
      store.dispatch("clearLogoContext");
      // Clean up all stored unsubscribe functions
      unsubscribeFunctions.value.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") {
          unsubscribe();
        }
      });
    });

    return {
      navbarVisible,
      user,
      currentOrgUID,
      activeTab,
      toggleMessages,
      areMessagesOpen,
      unreadMessagesCount,
      logoUrl,
      isLogoValid,
      handleImageError,
      displayKey,
      currentCoinSymbol,
      coinKey,
      isRotating,
      rotateClass,
      monetaryParameters,
      userUid,
      userAuth,
      goBack,
    };
  },
};
</script>

<style scoped>
.navbar .logo-image {
  height: var(--control-height-default);
  width: auto;
  border-radius: var(--radius-sm);
}

.userProfileLink {
  max-height: var(--control-height-default);
  width: -webkit-fill-available;
}

#placeholderspaceformenuAbsolute {
  min-width: calc(var(--control-height-default) - var(--space-4));
}

.navIconCont {
  display: flex;
  width: calc(100% - var(--control-height-default));
  transform: translateX(var(--space-3));
  justify-content: flex-end;
  transition: transform 0.3s ease-in-out;
  align-items: center;
}

.navIconCont .iconColumn {
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  justify-content: center;
  position: relative; /* Added for positioning notification dot */
}

.backArrow {
  width: var(--control-height-dense);
}

.notification-dot {
  position: absolute;
  top: calc(var(--control-height-default) / 2 - 8px);
  right: 1px;
  min-width: 16px;
  max-width: 16px;
  height: 16px;
  background-color: var(--color-notification-unread-surface);
  border-radius: 50%;
  color: var(--color-surface-canvas);
  text-align: center;
  font-size: 12px;
  padding: 0 4px;
  border: 1px solid var(--color-surface-canvas);
}

.navIconCont .iconColumn svg use {
  box-shadow: var(--shadow-surface-raised);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  justify-content: center;
}

.navIconCont .iconColumn.active {
  box-shadow: var(--shadow-surface-recessed-compact);
}

.navIconCont .iconColumn svg {
  height: var(--control-height-dense);
  width: var(--control-height-dense);
}

.circle {
  width: var(--control-height-default);
  height: var(--control-height-default);
  transition: transform 0.15s ease-in-out;
}

.coin-flip-enter-active,
.coin-flip-leave-active {
  animation: spin 0.15s linear forwards;
}

.rotate-to-90 {
  animation: rotateTo90 0.15s linear forwards;
}

.rotate-to-0 {
  animation: rotateTo0 0.15s linear forwards;
}

.wallet-icon {
  position: relative;
}

.small-coin-symbol {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-57%, -29%);
  width: 15px;
  height: 15px;
  padding: 9px;
  border: 0px transparent;
}

.logo-coin-rotate-enter-active,
.logo-coin-rotate-leave-active {
  animation: rotateTo90 0.3s linear forwards,
    rotateTo0 0.3s linear 0.3s forwards;
}

@keyframes rotateTo90 {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(90deg);
  }
}

@keyframes rotateTo0 {
  0% {
    transform: rotateY(90deg);
  }
  100% {
    transform: rotateY(0);
  }
}

@media (max-width: 590px) {
  .navIconCont {
    transform: none;
  }
}
</style>
